import Preloader from "src/components/Shared/LoadingAnimations/Preloader";
import React, { useEffect, useState, useCallback, useRef } from "react";
import NoData from "src/components/Shared/NoData/NoData";
import ClientSetupItem from "./ClientSetupItem";
import { apiRequest } from "src/async/apiUtils";

const ClientSetup = ({ details, statuses, setLists = () => {} }) => {
  const [steps, setSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const stepInterval = useRef(null);
  const controllerRef = useRef(null);

  const fetchClientStepData = useCallback(
    async (isFirstTime = false) => {
      if (!details?._id) return;

      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      controllerRef.current = new AbortController();

      if (isFirstTime) {
        setIsLoading(true);
      }

      try {
        if (details?.status !== "inactive") {
          setLists((prev) =>
            prev.map((item) => {
              if (item._id === details._id && item.status !== "inactive") {
                return { ...item, status: "in_progress" };
              }
              return item;
            }),
          );
        }

        const { data } = await apiRequest("GET", `/clients/${details._id}/steps`, {}, { signal: controllerRef.current.signal });

        const stepsData = data?.data?.steps || [];
        setSteps(stepsData);

        const allCompleted = stepsData.length > 0 && stepsData.every((s) => s.status === "completed");
        const anyIncomplete = stepsData.some((s) => s.status !== "completed");

        if (details?.status !== "inactive") {
          setLists((prev) =>
            prev.map((item) => {
              if (item._id === details._id && item.status !== "inactive") {
                if (allCompleted) {
                  return { ...item, status: "active" };
                } else if (anyIncomplete) {
                  return { ...item, status: "in_progress" };
                }
              }
              return item;
            }),
          );
        }

        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    },
    [details?._id, details?.status, setLists],
  );

  useEffect(() => {
    fetchClientStepData(true);
  }, [fetchClientStepData]);

  useEffect(() => {
    const hasIncompleteSteps = steps.some((item) => item.status !== "completed");

    if (stepInterval.current) {
      clearInterval(stepInterval.current);
      stepInterval.current = null;
    }

    if (hasIncompleteSteps) {
      stepInterval.current = setInterval(() => fetchClientStepData(false), 5000);
    }

    return () => {
      if (stepInterval.current) {
        clearInterval(stepInterval.current);
        stepInterval.current = null;
      }
    };
  }, [steps, fetchClientStepData]);

  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="relative">
        <Preloader color="text-gray-400 fill-gray-700" />
      </div>
    );
  }

  return (
    <div className="relative">
      {steps.length > 0 ? (
        <div className="divide-y divide-gray-200">
          {steps.map((item) => (
            <ClientSetupItem
              key={item._id}
              item={item}
              details={details}
              statuses={statuses}
              refreshData={fetchClientStepData}
            />
          ))}
        </div>
      ) : (
        <NoData />
      )}
    </div>
  );
};

export default ClientSetup;
